import React from "react";
import styles from "../../attorney/components/Sidebar/Sidebar.module.css"; // Shared sidebar styles
import { Book, CircleHelp, User } from "lucide-react";
import ChangelogButton from "@/shared/components/ChangelogButton/ChangelogButton";

// Icons
import analyticsIcon from "../../../assets/icons/analytics.svg";
import analyticsWhiteIcon from "../../../assets/icons/analyticsWhite.svg";
import clipboardIcon from "../../../assets/icons/clipboard.svg";
import clipboardWhiteIcon from "../../../assets/icons/clipboardWhite.svg";
import trackIcon from "../../../assets/icons/track.svg";
import trackWhiteIcon from "../../../assets/icons/trackWhite.svg";
import calendarIcon from "../../../assets/icons/calendar.svg";
import calendarWhiteIcon from "../../../assets/icons/calendarWhite.svg";
// Note: Support now uses Lucide User icon instead of help.svg

// Logo
const brandLogoPath = "/Logo_WhiteL.png";

export type AdminView = "dashboard" | "billing" | "entries" | "calendar" | "support" | "faq";

export interface AdminSidebarProps {
  darkMode: boolean;
  sidebarOpen: boolean;
  currentView: AdminView;
  onChangeView: (view: AdminView) => void;
  permissions: {
    overview: boolean;
    billing: boolean;
    entries: boolean;
    calendar: boolean;
    support: boolean;
  };
  onSwitchToAttorney?: () => void;
}

const AdminSidebar: React.FC<AdminSidebarProps> = ({
  darkMode,
  sidebarOpen,
  currentView,
  onChangeView,
  permissions,
  onSwitchToAttorney,
}) => {
  const brandDarkSidebar = true;
  const useLightIcons = brandDarkSidebar || darkMode;

  const navItems: Array<{
    view: AdminView;
    label: string;
    icon: string;
    iconWhite: string;
    permissionKey: keyof typeof permissions;
  }> = [
    {
      view: "dashboard",
      label: "Dashboard",
      icon: analyticsIcon,
      iconWhite: analyticsWhiteIcon,
      permissionKey: "overview",
    },
    {
      view: "billing",
      label: "Billing",
      icon: clipboardIcon,
      iconWhite: clipboardWhiteIcon,
      permissionKey: "billing",
    },
    {
      view: "entries",
      label: "Entries",
      icon: trackIcon,
      iconWhite: trackWhiteIcon,
      permissionKey: "entries",
    },
    {
      view: "calendar",
      label: "Calendar",
      icon: calendarIcon,
      iconWhite: calendarWhiteIcon,
      permissionKey: "calendar",
    },
  ];

  return (
    <div
      className={`${styles.sidebar} ${!sidebarOpen ? styles.collapsed : ""}`}
    >
      <div className={styles.navContainer}>
        <div className={styles.navTop}>
          {/* Logo */}
          <div className={styles.navItem}>
            <div
              className={`${styles.viewButtonLogo} ${!sidebarOpen ? styles.iconOnly : ""}`}
              style={{ width: "auto", maxWidth: "none" }}
            >
              <img src={brandLogoPath} alt="Hourglass" className={styles.sidebarBrandLogo} />
            </div>
          </div>

          {/* Navigation Items */}
          {navItems.map((item) => {
            // Only show if user has permission for this view
            if (!permissions[item.permissionKey]) return null;

            return (
              <div key={item.view} className={styles.navItem}>
                <div
                  className={`${styles.viewButton} ${styles.brandSidebarButton} ${
                    currentView === item.view ? styles.viewButtonSelected : ""
                  } ${!sidebarOpen ? styles.iconOnly : ""}`}
                  onClick={() => onChangeView(item.view)}
                >
                  <img
                    src={useLightIcons ? item.iconWhite : item.icon}
                    alt={`${item.label} icon`}
                    className={styles.viewButtonIcon}
                  />
                  {sidebarOpen && item.label}
                </div>
              </div>
            );
          })}

          {/* Support - uses Lucide User icon */}
          {permissions.support && (
            <div className={styles.navItem}>
              <div
                className={`${styles.viewButton} ${styles.brandSidebarButton} ${
                  currentView === "support" ? styles.viewButtonSelected : ""
                } ${!sidebarOpen ? styles.iconOnly : ""}`}
                onClick={() => onChangeView("support")}
              >
                <User
                  className={styles.viewButtonIcon}
                  color="white"
                  strokeWidth={1.5}
                />
                {sidebarOpen && "Support"}
              </div>
            </div>
          )}
        </div>

        {/* Bottom section */}
        <div className={styles.navBottom}>
          {/* FAQ */}
          <div className={styles.navItem}>
            <div
              className={`${styles.viewButton} ${styles.brandSidebarButton} ${
                currentView === "faq" ? styles.viewButtonSelected : ""
              } ${!sidebarOpen ? styles.iconOnly : ""}`}
              onClick={() => onChangeView("faq")}
            >
              <CircleHelp
                className={styles.viewButtonIcon}
                color="white"
                strokeWidth={1.5}
              />
              {sidebarOpen && "FAQ"}
            </div>
          </div>

          {/* Changelog/Updates */}
          <div className={styles.navItem}>
            <ChangelogButton
              variant={sidebarOpen ? "secondary" : "icon"}
              text={sidebarOpen ? "Updates" : undefined}
            />
          </div>

          {onSwitchToAttorney && (
            <div className={styles.navItem}>
              <div
                className={`${styles.viewButton} ${styles.brandSidebarButton} ${!sidebarOpen ? styles.iconOnly : ""}`}
                onClick={onSwitchToAttorney}
              >
                <Book
                  className={styles.viewButtonIcon}
                  color={useLightIcons ? "white" : "currentColor"}
                  strokeWidth={1.5}
                />
                {sidebarOpen && "Attorney View"}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminSidebar;
